import type { DtActionsAtom } from '@/components/pages/backoffice/_klb-penyuluh/_kecamatan/state/atoms'
import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { PotensiWilayahDataType } from './types'
import type { MutationPotensiWilayahValues } from './validations'

const QUERY_KEY = (key?: string | Record<string, any>) =>
  ['potensi-wilayah', key] as const

const getAll = (
  params?: DtActionsAtom
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PotensiWilayahDataType>>
> => ({
  queryKey: QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PotensiWilayahDataType>>
      >(`kelembagaan/bpp/${params?.id_bpp}/potensi-wilayah`, {
        params: {
          page: params?.page,
          per_page: params?.perPage || undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string,
  id_bpp?: string
): ServiceQueryOptionType<ServiceGetByIdResponse<PotensiWilayahDataType>> => ({
  queryKey: QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetByIdResponse<PotensiWilayahDataType>
      >(`kelembagaan/bpp/${id_bpp ?? ''}/potensi-wilayah/${id}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PotensiWilayahDataType>,
  MutationPotensiWilayahValues
> => ({
  mutationKey: QUERY_KEY(undefined),
  mutationFn: async (data: MutationPotensiWilayahValues) => {
    try {
      const response = await api.post(
        `kelembagaan/bpp/${data.id_bpp ?? ''}/potensi-wilayah`,
        {
          ...data,
          id_komoditas: data.id_komoditas?.value
        }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PotensiWilayahDataType>,
  MutationPotensiWilayahValues
> => ({
  mutationKey: QUERY_KEY(undefined),
  mutationFn: async (data: MutationPotensiWilayahValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<PotensiWilayahDataType>
      >(
        `kelembagaan/bpp/${data?.id_bpp ?? ''}/potensi-wilayah/${data.id_potensi_wilayah}`,
        {
          ...data,
          id_komoditas: data.id_komoditas?.value
        }
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<PotensiWilayahDataType>,
  { id: string; id_bpp: string }
> => ({
  mutationFn: async (data: { id: string; id_bpp: string }) => {
    try {
      const response = await api.delete<
        ServiceGetByIdResponse<PotensiWilayahDataType>
      >(`kelembagaan/bpp/${data?.id_bpp ?? ''}/potensi-wilayah/${data.id}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const potensiWilayah = {
  queryKey: [QUERY_KEY(undefined)[0]],
  getAll,
  getById,
  create,
  update,
  deleteData
}
